import { useCallback, useState } from 'react';
import { AIGoalPlan } from '../services/aiService';
import { applyAIPlan } from '../services/aiPlanService';
import { logger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';

export interface AIPlanApplyProgress {
  phase: 'idle' | 'applying' | 'done' | 'error';
  message?: string;
}

interface UseAIPlanApplyOptions {
  loadGoals: () => Promise<void>;
  navigateToGoal: (goalId: string) => void;
  onBeforeApply?: () => void;
}

export function useAIPlanApply({
  loadGoals,
  navigateToGoal,
  onBeforeApply,
}: UseAIPlanApplyOptions) {
  const [progress, setProgress] = useState<AIPlanApplyProgress>({ phase: 'idle' });
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);

  const applyPlan = useCallback(
    async (aiData: AIGoalPlan) => {
      onBeforeApply?.();
      setProgress({ phase: 'applying', message: 'Creating goal structure…' });

      try {
        const draftId = activeDraftId || aiData.draft_id;
        const result = await applyAIPlan({
          title: aiData.title,
          description: aiData.description,
          estimated_completion_date: aiData.estimated_completion_date,
          sub_goals: aiData.sub_goals,
          draft_id: draftId,
        });

        trackEvent('goal_created', { method: 'ai' });
        trackEvent('ai_plan_applied', {
          subgoal_count: aiData.sub_goals.length,
          todo_count: aiData.sub_goals.reduce((sum, subGoal) => sum + subGoal.todos.length, 0),
        });
        setActiveDraftId(null);
        await loadGoals();
        setProgress({ phase: 'done' });
        navigateToGoal(result.goal.id);
      } catch (error) {
        setProgress({ phase: 'error' });
        logger.error('AI plan apply failed', error);
        throw error;
      } finally {
        setTimeout(() => setProgress({ phase: 'idle' }), 400);
      }
    },
    [activeDraftId, loadGoals, navigateToGoal, onBeforeApply]
  );

  return {
    progress,
    activeDraftId,
    setActiveDraftId,
    applyPlan,
  };
}
